import React, { useEffect, useState } from 'react';
import { onSnapshot, collection, doc, getDoc } from '@firebase/firestore';
import { db, functions } from '../service/Firebase-config';
import { List, ListItemButton, ListItemIcon, ListItemText, Switch, Box, CircularProgress, Avatar, Tooltip } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { stringAvatar } from '../common/util';
import { styled } from '@mui/system';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';

export default function ListPerson({ searchQuery, statusFilter }) {
  const { selectedUser, setSelectedUser, setTotalStars } = useGlobalContext();
  const { group } = useHomeworkContext();
  const [people, setPeople] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, group), (snapshot) => {
      const sortedPeople = snapshot.docs
        .map(doc => ({ ...doc.data(), id: doc.id }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)); // Sort by displayName
      setPeople(sortedPeople);
    });
    return unsub;
  }, [group]);

  const handleClick = (user) => {
    setSelectedUser(user);
    setTotalStars(user.totalStars);
  };

  const handleToggleActive = async (userId, isActive, role) => {
    setLoadingUsers(prev => ({ ...prev, [userId]: true }));
    try {
      const action = isActive ? 'handleDisableUser' : 'handleReactivateUser';
      const toggleUser = httpsCallable(functions, action);
      await toggleUser({ uid: userId, isStudent: role === 'student' });

      const userDocRef = doc(db, group, userId);
      const userDoc = await getDoc(userDocRef);
      const updatedUserData = userDoc.data();

      setPeople(prevPeople => prevPeople.map(person =>
        person.id === userId ? { ...person, ...updatedUserData } : person
      ));

      setSelectedUser(updatedUserData);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingUsers(prev => ({ ...prev, [userId]: false }));
    }
  };

  const filteredPeople = people.filter(user => {
    const matchesQuery = user?.displayName?.toLowerCase().includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' && user.active) || (statusFilter === 'inactive' && !user.active);
    return matchesQuery && matchesStatus;
  });

  const TruncatedText = styled('span')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    maxWidth: '200px', // Adjust the max-width as needed
  });

  return (
    <Box 
      sx={{
        height: '78vh', // 전체 화면 높이
        overflowY: 'auto', // 세로 스크롤 활성화
        overflowX: 'hidden', // 가로 스크롤 비활성화
        borderTop: '1px solid #ddd', // 선택적으로 테두리 추가
        borderRadius: '4px', // 선택적으로 모서리 둥글게
      }}
    >
      <List>
        {filteredPeople && filteredPeople.map(user => (
          <ListItemButton
            key={user?.id}
            onClick={() => handleClick(user)}
            selected={selectedUser?.id === user?.id}
          >
            <ListItemIcon>
              <Tooltip title={user.displayName} arrow>
                {user.photoUrl || user.role !== 'student' ? (
                  <Avatar src={user.photoUrl} />
                ) : (
                  <Avatar {...stringAvatar(user.displayName)} />
                )}
              </Tooltip>
            </ListItemIcon>
            <ListItemText
              primary={loadingUsers[user.id] ? <CircularProgress size={24} /> : (
                <TruncatedText>{user.displayName}</TruncatedText>
              )}
            />
            {statusFilter === 'all' && (
              <Box sx={{ marginLeft: 'auto' }}>
                <Switch
                  checked={user.active}
                  onChange={() => handleToggleActive(user.id, user.active, user.role)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </Box>
            )}
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}
